import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ChatCallPanel from "src/scenes/Chat/components/ChatCallPanel";
import { getOpenerCallWindowBridge } from "src/scenes/Chat/services/CallWindowBridge";
import { useCallWindowRuntime } from "src/scenes/Chat/hooks/useCallWindowRuntime";
import { useMeetingCallNotes } from "src/scenes/Meet/useMeetingCallNotes";
import { UserKeys } from "src/constants/userDetails";
import { createMeetingEventMetadata } from "src/scenes/Chat/utils/meetingCallEventContract";
import { sendMeetingNotifications } from "src/scenes/Chat/utils/meetingCallNotifications";

const ErrorPage = styled.main`
    align-items: center;
    background: #15171c;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: center;
    min-height: 100vh;
    padding: 32px;
    text-align: center;
`;

const CloseButton = styled.button`
    background: #fff;
    border: 0;
    border-radius: 4px;
    color: #15171c;
    cursor: pointer;
    font-weight: 700;
    padding: 10px 18px;
`;

const useBridgeState = bridge => {
    const [, forceUpdate] = useState(0);

    useEffect(() => bridge?.subscribe?.(() => forceUpdate(value => value + 1)), [bridge]);
};

const getCallWindowTitle = (descriptor, bridge) => {
    const currentMatrixUserId = bridge.matrixClient?.getUserId?.();
    const otherUser = descriptor.chat?.users?.find(user => user.matrixUserId !== currentMatrixUserId);
    const otherUserName = `${otherUser?.employeeDetails?.firstName || otherUser?.firstName || ""} ${otherUser?.employeeDetails?.lastName || otherUser?.lastName || ""}`.trim();
    const conversationName = descriptor.chat?.name || descriptor.chat?.groupName || otherUserName || "Call";
    const companyName = bridge.currentUser?.[UserKeys.COMPANY_NAME];

    return [conversationName, companyName, "Me Business"].filter(Boolean).join(" - ");
};

const CallWindow = ({ bridge, callToken, descriptor }) => {
    useBridgeState(bridge);
    const runtime = useCallWindowRuntime({ bridge, callToken, descriptor });
    const hasNotesUpdateStatusRef = useRef("idle");
    const publishHasNotesUpdate = useCallback(async () => {
        runtime.markCallHasNotes();
        if (
            hasNotesUpdateStatusRef.current !== "idle" ||
            !descriptor.meetingUuid ||
            !descriptor.chat?.originatingMatrixRoomId ||
            !descriptor.chat?.matrixRoomId
        ) {
            return;
        }

        hasNotesUpdateStatusRef.current = "pending";
        const meetingMetadata = createMeetingEventMetadata({
            meetingUuid: descriptor.meetingUuid,
            callId: descriptor.callId,
            chatUuid: descriptor.chat.uuid,
            chatRoomId: descriptor.chat.originatingMatrixRoomId,
            matrixRoomId: descriptor.chat.matrixRoomId
        });
        try {
            await sendMeetingNotifications(bridge.matrixClient, {
                callId: descriptor.callId,
                lifetime: 0,
                meetingMetadata,
                mode: "notification",
                recipientIds: [],
                roomId: descriptor.chat.originatingMatrixRoomId,
                senderTimestamp: Date.now(),
                summary: { hasNotes: true }
            });
            hasNotesUpdateStatusRef.current = "published";
        } catch (error) {
            hasNotesUpdateStatusRef.current = "idle";
            console.warn("Unable to publish the call notes status update.", error);
        }
    }, [
        bridge.matrixClient,
        descriptor.callId,
        descriptor.chat?.matrixRoomId,
        descriptor.chat?.originatingMatrixRoomId,
        descriptor.chat?.uuid,
        descriptor.meetingUuid,
        runtime.markCallHasNotes
    ]);
    const meetingNotes = useMeetingCallNotes(
        bridge.matrixClient,
        descriptor.meetingUuid ? descriptor.chat?.matrixRoomId || "" : "",
        descriptor.meetingUuid ? publishHasNotesUpdate : undefined
    );
    const bridgedCallNotes = useMemo(() => bridge.callNotes || [], [bridge, bridge.callNotes]);
    const callNotes = descriptor.meetingUuid ? meetingNotes.notes : bridgedCallNotes;
    const callLobby = runtime.callState.status === "idle" || runtime.callState.status === "inbound"
        ? { isOpen: true, chat: descriptor.chat, popupBlocked: false }
        : { isOpen: false, chat: descriptor.chat, popupBlocked: false };
    const meetingChat = descriptor.meetingUuid
        ? { ...descriptor.chat, meetingUuid: descriptor.meetingUuid }
        : descriptor.chat;

    useEffect(() => {
        document.title = getCallWindowTitle(descriptor, bridge);
    }, [bridge, bridge.currentUser, descriptor]);

    return (
        <ChatCallPanel
            dedicatedRuntime
            callState={runtime.callState}
            callNotes={callNotes}
            isSendingCallNote={descriptor.meetingUuid
                ? meetingNotes.isSending
                : Boolean(bridge.isSendingCallNote)}
            onAddCallNote={descriptor.meetingUuid
                ? meetingNotes.addNote
                : body => bridge.addCallNote?.(body)}
            onDeleteCallNote={descriptor.meetingUuid ? meetingNotes.deleteNote : undefined}
            onEditCallNote={descriptor.meetingUuid ? meetingNotes.editNote : undefined}
            currentChat={meetingChat}
            currentUser={bridge.currentUser}
            matrixClient={bridge.matrixClient}
            currentMatrixUserId={bridge.matrixClient.getUserId?.()}
            callLobby={callLobby}
            isSendingCallAction={runtime.isConnecting}
            screenShareIsActive={runtime.screenShareIsActive}
            localStream={runtime.localStream}
            localScreenShareStream={runtime.localScreenShareStream}
            remoteStreams={runtime.remoteStreams}
            onCancelLobby={runtime.cancel}
            onStartFromLobby={runtime.connect}
            onFocusWindow={() => window.focus()}
            onSetLocalAudioEnabled={runtime.setLocalAudioEnabled}
            onSetLocalMicrophoneDevice={runtime.setLocalMicrophoneDevice}
            onSetLocalVideoEnabled={runtime.setLocalVideoEnabled}
            onSetLocalCameraDevice={runtime.setLocalCameraDevice}
            onSetLocalSpeakerDevice={runtime.setLocalSpeakerDevice}
            onToggleScreenShare={runtime.toggleScreenShare}
            onSetLocalBackgroundEffect={runtime.setLocalBackgroundEffect}
            onAnswerFromLobby={runtime.connect}
            onHangup={runtime.hangup}
            onReject={runtime.cancel}
        />
    );
};

const CallWindowPage = () => {
    const callToken = new URLSearchParams(window.location.search).get("callToken") || "";
    const bridge = getOpenerCallWindowBridge();
    const descriptor = bridge?.getCall?.(callToken);

    useEffect(() => {
        document.body.style.margin = "0";
        document.body.style.background = "#15171c";
    }, []);

    if (!window.opener || !bridge || !bridge.matrixClient || !descriptor) {
        return (
            <ErrorPage>
                <h1>Call unavailable</h1>
                <p>The call could not connect to the active Me Business session.</p>
                <CloseButton type="button" onClick={() => window.close()}>Close</CloseButton>
            </ErrorPage>
        );
    }

    return <CallWindow bridge={bridge} callToken={callToken} descriptor={descriptor} />;
};

export default CallWindowPage;
